import { getDomesticCountryCode } from './countryCode';

export const pluralize = (count: number, singular: string, plural?: string) => {
  if (count === 1) {
    return `${count} ${singular}`;
  } else {
    return `${count} ${plural ?? singular + 's'}`;
  }
};

export const pluralLabel = (count: number, singular: string, plural?: string) => {
  if (count === 1) {
    return singular;
  } else {
    return plural ?? singular + 's';
  }
};

export const capitalize = (str: string): string =>
  `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;

const statesByCountryCache = new Map<string, any[]>();

const looksLikeStateCode = (state: string) => /^[A-Z]{2,3}$/.test(state);

export const formatParserResponse = async (address, countries, dataProvider) => {
  const domesticCountryCode = getDomesticCountryCode();

  const getCountry = () => {
    if (address.countryISO) {
      return address.countryISO;
    }

    if (address.country?.length === 2) {
      return address.country;
    }

    const formattedCountry = address.country
      ?.trim()
      ?.split(' ')
      ?.map((item) => capitalize(item))
      ?.join(' ');

    const countryCode = countries?.find((country) => country.name === formattedCountry)?.code;

    if (countryCode) {
      return countryCode;
    }

    return formattedCountry;
  };

  const getStateProvinceCode = async (countryCode: string) => {
    const stateValue =
      address.state ??
      address.stateProvinceCode ??
      address.stateProvince?.code ??
      address.stateProvince?.name;

    const normalizedState = stateValue?.trim();
    if (!normalizedState) return '';

    const upperCaseState = normalizedState.toUpperCase();
    if (looksLikeStateCode(upperCaseState)) {
      return upperCaseState;
    }

    const countryId = countries?.find((country) => country.code === countryCode)?.id;
    if (!countryId || !dataProvider?.getManyReference) {
      return upperCaseState;
    }

    try {
      const cachedStates = statesByCountryCache.get(countryId);
      const states =
        cachedStates ??
        (await dataProvider.getManyReference('states', { id: countryId }))?.data ??
        [];

      if (!cachedStates) {
        statesByCountryCache.set(countryId, states);
      }

      const lowerCaseState = normalizedState.toLowerCase();
      const matchedState = states.find(
        (state) =>
          state?.name?.toLowerCase() === lowerCaseState ||
          state?.code?.toLowerCase() === lowerCaseState
      );

      if (matchedState?.code) {
        return matchedState.code;
      }
    } catch (error) {
      console.warn('Failed to resolve state code', error);
    }

    return upperCaseState;
  };

  const countryCode = getCountry() || domesticCountryCode;

  return {
    addressLine1: address.addressLine || '',
    city: address.city || '',
    countryCode,
    zipCode: address.zipCode || '',
    stateProvinceCode: await getStateProvinceCode(countryCode),
    name: address.name || '',
    addressLine2: address.unit || '',
    phone: address.phone || '',
    email: address.email || '',
    companyName: address.company || '',
  };
};

export const snakeCaseToSimpleText = (str: string) => {
  return str.replace(/_/g, ' ');
};

export const normalizeStatus = (status: string): string => {
  const capitalizeStatus = capitalize(status);

  return snakeCaseToSimpleText(capitalizeStatus);
};
